<template>
  <div class="p-10 pb-8 md:pt-8 md:pb-10 md:px-16 xl:pl-32">
    <!-- Estimation type (glasses/contacts) -->
    <p class="hidden md:block mb-2 text-sm font-bold leading-3 tracking-1.25px text-black-high-emphasis">
      {{ content.estimateType}}
    </p>

    <!-- Out of pocket costs -->
    <h4 class="mb-2 md:mb-10 text-xl md:text-34 font-bold md:font-normal md:leading-10 tracking-0.25px text-black-darkest text-center md:text-left">
      {{ content.estimatedCostText }}
      <p class="hidden md:inline text-primary-dark" data-testid="estimated-costs-value">
        ${{ estimateCostValue }}
      </p>
    </h4>

    <h3 class="md:hidden mb-2 text-center text-34 text-primary-dark font-extrabold">
      ${{ estimateCostValue }}
    </h3>

    <h5 class="md:hidden mb-10 text-center text-xl tracking-0.15px text-primary-dark font-extrabold" data-testid="savings">
      {{ content.savingsText }} ${{ savingsValue }}!
    </h5>

    <div class="md:flex items-center" >
      <!-- Product image -->
      <div
        v-if="productImage?.isIcon"
        class="summary-image w-full h-full text-primary fill-current"
        v-dompurify-html:svg="iconSource"
      />
      <img
        v-else
        :alt="productImage?.alt"
        :src="productImage?.source"
        class="summary-image"
      />

      <div class="mb-auto flex-grow">
        <p class="mb-4 md:mb-6 text-xl md:text-2xl tracking-0.15px text-black-darkest">
          {{ content.yourSelectionText }}
        </p>
        <!-- Selection summary -->
        <div
          v-for="(selection, index) in selectionSummary || []"
          :key="index"
          class="flex text-base md:text-xl tracking-0.5px mb-1 md:mb-2"
        >
          <p class="font-extrabold md:font-bold text-gray-darkest mr-1 md:mr-2 flex-shrink-0">
            {{ selection.label }}
          </p>
          <p class="text-gray-medium">
            {{ selection.answer }}
          </p>
        </div>

        <!-- Amount saved -->
        <h5 class="hidden md:block mt-6 text-xl tracking-0.15px text-primary-dark font-bold">
          {{ content.savingsText }} ${{ savingsValue }}!
        </h5>

        <!-- Edit/Restart buttons -->
        <div class="flex flex-wrap gap-6 mt-4">
          <KButton v-if="isEditButtonVisible" textOnly theme="primary" size="md" @click="editSelections">
            {{ content.editButtonText }}
          </KButton>
          <KButton textOnly theme="primary" size="md" @click="restart">
            <template #prepend>
               <KIcon name="restart" class="restart-icon"/>
            </template>
            {{ content.restartButtonText }}
          </KButton>
        </div>
        <div v-if="allowanceAmount !== 0" class="flex flex-wrap gap-6 mt-4 break-words" v-dompurify-html="global.globalAllowanceContent.globalAllowanceDisclaimer" />
        <div v-else-if="startDeclineAmount !== 0" class="flex flex-wrap gap-6 mt-4 break-words" v-dompurify-html="global.declineBalanceContent.declineBalanceDisclaimer" />
        <div v-else-if="pricingSummary.finalPageDisclaimer" class="flex flex-wrap gap-6 mt-4 break-words">
          {{ pricingSummary.finalPageDisclaimer }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { EstimatedCostsSection, Question } from '@/model'
import KIcon from '@/components/atoms/Icon/index.vue';
import KButton from '@/components/atoms/Button/index.vue';
import { useFlow, useGlobal } from '@/store'
import { useRoute, useRouter } from 'vue-router'
import { ServiceTypes } from '@/enums'
import KImage from '@/types/KImage'
import useRemoteIcon from '@/hooks/useRemoteIcon'
import { trackEvent } from '@/utils/analytics'
import { addAdobeDataLayerEventEditSelection } from '@/utils/adobe-analytics-data-layer'

export default defineComponent({
  components: {
    KIcon,
    KButton
  },
  props: {
    content: {
      type: Object as () => EstimatedCostsSection,
      required: true
    }
  },
  setup (props) {
    const router = useRouter()
    const route = useRoute()
    const global = useGlobal()
    const {
      goToEditSelectionQuestion,
      startFlow,
      flow,
      pricingSummary,
      selectedAnswers,
      serviceType,
      selectionSummary
    } = useFlow()
    const isEditButtonVisible = flow?.hasEditSelectionsQuestion
    const estimateCostValue = pricingSummary.totalMoop
    const savingsValue = pricingSummary.saving
    const allowanceAmount = pricingSummary.allowanceAmount
    const startDeclineAmount = pricingSummary.startDeclineAmount
    let productImage: KImage | undefined

    if (serviceType === ServiceTypes.exam) {
      productImage = props.content.productsImages[0]
    } else {
      const flowStartQuestion = flow?.getEditSelectionsQuestion() as Question
      const answerImage = selectedAnswers[flowStartQuestion.uid]?.selections[0].answer.image
      productImage = props.content.productsImages.find(i => i.alt === answerImage?.alt) || answerImage
    }

    const iconSource = productImage?.isIcon && productImage.source
      ? useRemoteIcon(productImage.source)
      : null

    const restart = () => {
      trackEvent('event-restart')
      startFlow()
      router.replace({ path: 'flow', query: route.query })
    }

    const editSelections = () => {
      // trackEvent('event-edit-selection')
      addAdobeDataLayerEventEditSelection()
      goToEditSelectionQuestion()
      router.replace({ path: 'flow', query: route.query })
    }

    return {
      estimateCostValue,
      savingsValue,
      allowanceAmount,
      startDeclineAmount,
      selectionSummary,
      productImage,
      isEditButtonVisible,
      iconSource,
      restart,
      editSelections,
      pricingSummary,
      global
    }
  }
})
</script>

<style scoped>
.summary-image {
  @apply mx-auto flex-grow-0 mb-6 px-6;
}

@screen md {
  .summary-image {
    @apply  pl-0 mb-0;
    max-width: 40%;
  }
}

.restart-icon {
  margin-right: 6px;
  @apply my-1 fill-current w-4 h-4;
}
</style>
